import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { CheckCircleIcon } from '@heroicons/react/24/outline';
import { apiClient } from '../services/api/client';
import AuthLayout from '../components/layout/AuthLayout';
import { Button, Text } from '../components/ui';
import { Input } from '../components/forms';

const forgotSchema = z.object({
  email: z.string().email('Valid email is required'),
});

type ForgotFormData = z.infer<typeof forgotSchema>;

const ForgotPassword: React.FC = () => {
  const navigate = useNavigate();
  const [submitted, setSubmitted] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotFormData>({
    resolver: zodResolver(forgotSchema),
  });

  const mutation = useMutation({
    mutationFn: async (data: ForgotFormData) => {
      const response = await apiClient.post('/auth/password-reset/request', { email: data.email });
      return response.data;
    },
    onSuccess: () => {
      setSubmitted(true);
    },
  });

  if (submitted) {
    return (
      <AuthLayout title="Check Your Email" subtitle="We've sent you a password reset link">
        <div className="text-center">
          <CheckCircleIcon className="mx-auto h-16 w-16 text-green-500 mb-4" />
          <Text className="text-gray-600 mb-6">
            If an account exists with that email, you'll receive a password reset link shortly.
          </Text>
          <Button variant="primary" fullWidth onClick={() => navigate('/login')}>
            Back to Login
          </Button>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Forgot Password" subtitle="Enter your email to reset your password">
      <form onSubmit={handleSubmit((data) => mutation.mutate(data))} className="space-y-4">
        <Input
          {...register('email')}
          label="Email Address"
          type="email"
          placeholder="your@email.com"
          error={errors.email?.message}
          autoFocus
        />

        {mutation.isError && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
            <Text className="text-sm text-red-600">
              Failed to send reset email. Please try again.
            </Text>
          </div>
        )}

        <Button
          type="submit"
          variant="primary"
          fullWidth
          size="lg"
          isLoading={mutation.isPending}
        >
          Send Reset Link
        </Button>

        <div className="text-center">
          <button
            type="button"
            onClick={() => navigate('/login')}
            className="text-sm text-primary-600 hover:text-primary-500"
          >
            Back to Login
          </button>
        </div>
      </form>
    </AuthLayout>
  );
};

export default ForgotPassword;
